/**
 * Automatic sliders can be configured by adding the "interval" property to the HTML. If this property is set,
 * and the user has no preference for reduce-motion, the carousel will start sliding when it is scrolled in to
 * view and will stop sliding when it's out of view. If the interval property is set a "play/pause" button will
 * also be created, which will allow to play/pause without clearing the data of what the original interval
 * should be.
 */

import {
  LitElement,
  html,
  css,
  unsafeHTML,
  ref,
  createRef,
  paths,
  t,
  styles,
  utils,
} from '@hh/global.js'

const { prefersReducedMotion, fadeInAnimation } = utils
class HHSlider extends LitElement {
  static properties = {
    active: {
      reflect: true,
    },
    arrows: {
      type: Boolean,
    },
    hideButtons: {
      type: Boolean,
    },
    hideButtonsDesktop: {
      type: Boolean,
    },
    hideArrowsMobile: {
      type: Boolean,
    },
    noGap: {
      type: Boolean,
    },
    overlap: {
      type: Boolean,
    },
    fullWidth: {
      type: Boolean,
    },
    breakpoint: {
      type: Number,
    },
    interval: {
      type: Number,
    },
    _current: {
      state: true,
    },
    _total: {
      state: true,
    },
    _pages: {
      state: true,
    },
    shouldRun: {
      reflect: true,
      type: Boolean,
    },
  }

  $container = createRef()

  constructor() {
    super()

    this._total = 0
    this._current = 0
    this.items = []
    this._pages = []
    this.containerWidth = 0
    this._totalWidth = 0
    this.interval = 0
    this.shouldRun = false
    this._itemsPerPage = 0

    this.updateUI = this.updateUI.bind(this)
    this.handleResize = this.handleResize.bind(this)
    this.move = this.move.bind(this)

    this.startIntervalId = null
    this.intervalId = null
  }

  connectedCallback() {
    super.connectedCallback()

    setTimeout(() => {
      this.init()
    }, 100)
  }

  init() {
    this.shouldRun = this.interval > 0

    this.items = Array.from(this.querySelectorAll('[slot="item"]'))

    window.addEventListener('resize', this.handleResize)

    if (prefersReducedMotion()) {
      this.shouldRun = false
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (!prefersReducedMotion() && this.shouldRun) {
            this.startIntervalId = setTimeout(
              this.move,
              this.interval,
              1,
              this._pages
            )
          }

          if (!this.items.length)
            // Occasionally the items are not yet available
            // when the observer fires, so we check again
            this.items = Array.from(this.querySelectorAll('[slot="item"]'))

          fadeInAnimation(this.items)
        } else {
          clearTimeout(this.startIntervalId)
          clearTimeout(this.intervalId)
        }
      })
    })

    observer.observe(this)

    this._total = this.items.length

    this.observer = new ResizeObserver((...args) => {
      this.activate(...args)
    })

    setTimeout(() => {
      this.handleResize()
      this.$container?.value?.addEventListener('scroll', this.updateUI, {
        passive: true,
      })
      if (this.breakpoint) {
        this.observer.observe(document.body)
      }

      if (!this.items.length) return

      this._containerWidth = Math.ceil(
        this.closest('hh-slider').getBoundingClientRect().width
      )
      this._totalWidth = this._total * this.items[0].clientWidth
      this._itemsPerPage = this._containerWidth / this.items[0]?.clientWidth

      this._pages = Array(
        Math.ceil(this._totalWidth / this._containerWidth)
      ).fill(1)
    }, 0)
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    window.removeEventListener('resize', this.handleResize)

    this.$container?.value?.removeEventListener('scroll', this.updateUI)
    this.observer.disconnect()
  }

  activate([first, ...all]) {
    const { contentRect: { width = 0 } = {} } = first || {}
    if (this.breakpoint) {
      if (width < this.breakpoint && !this.active) {
        this.active = true
      } else if (width >= this.breakpoint && this.active) {
        this.active = false
      }
    }
  }

  updateUI() {
    if (!this._total) {
      return
    }

    if (
      this.$container?.value?.scrollLeft + this._containerWidth >
      this._totalWidth
    ) {
      this._current = this._pages.length - 1
    } else {
      setTimeout(() => {
        this._current = Math.round(
          this.$container?.value?.scrollLeft / this._containerWidth
        )
      }, 350)
    }
  }

  move(page) {
    if (page > this._pages.length - 1 || page < 0) {
      page = 0
    }

    const slideScrollPosition =
      page * this.items[0].clientWidth * this._itemsPerPage

    this._current = page
    this.$container?.value?.scrollTo({
      left: slideScrollPosition,
    })

    if (this.shouldRun) {
      this.intervalId = setTimeout(this.move, this.interval, this._current + 1)
    }
  }

  handleResize() {
    // the cards have a min width of 262, and a gap of 10
    // if the container is wider than all the cards and all the gaps,
    // it doesn't need to be a slider anymore

    if (!this.items.length) return

    this._containerWidth =
      this.closest('hh-slider').getBoundingClientRect().width
    this._totalWidth = this._total * this.items[0].clientWidth
    this._itemsPerPage = this._containerWidth / this.items[0].clientWidth

    this.active = this._containerWidth <= this._totalWidth
  }

  handlePlayPause() {
    const temp = this.shouldRun
    if (this.shouldRun) {
      clearTimeout(this.startIntervalId)
      clearTimeout(this.intervalId)
    } else {
      this.startIntervalId = setTimeout(
        this.move,
        this.interval,
        1,
        this._pages
      )
    }
    this.shouldRun = !temp
  }

  getPlayButton() {
    if (this.interval <= 0) return ''

    return html`<button
      class="play"
      aria-label="${this.shouldRun ? 'pause' : 'play'}"
      @click="${this.handlePlayPause}"
    >
      ${this.shouldRun ? unsafeHTML(paths.pause) : unsafeHTML(paths.play)}
    </button>`
  }

  render() {
    return html`
      ${this.getPlayButton()}
      <div
        class="container ${this.noGap && 'container--no-gap'} ${this.items
          .length === 1 && 'container--single-item'}"
        ${ref(this.$container)}
        role="list"
      >
        <slot class="motion-reduce" name="item"></slot>
      </div>
      ${this.arrows &&
      html`
        <div class="arrows ${this.hideArrowsMobile && 'hidden--sm hidden--md'}">
          <button
            @click="${() => this.move(this._current - 1)}"
            type="button"
            ?disabled="${this._current <= 0}"
            class="arrow arrow--prev"
            name="previous"
            aria-label="${t('accessibility.previous_slide', 'Previous')}"
          >
            ${unsafeHTML(paths.caret)}
          </button>
          <button
            @click="${() => this.move(this._current + 1)}"
            type="button"
            ?disabled="${this._current >= this._pages.length - 1}"
            class="arrow arrow--next"
            name="next"
            aria-label="${t('accessibility.next_slide', 'Next')}"
          >
            ${unsafeHTML(paths.caret)}
          </button>
        </div>
      `}
      ${this._pages.length > 1
        ? html`
            <ul
              class="carousel-buttons ${this.overlap &&
              'carousel-buttons--overlap'}  ${this.fullWidth &&
              'carousel-buttons--fullWidth'} ${this.hideButtonsDesktop &&
              'hidden--lg'} ${this.hideButtons && 'hidden'}"
            >
              ${this._pages.map(
                (item, index) =>
                  html`<li>
                    <button
                      class="carousel-button ${index === this._current &&
                      'carousel-button--current'}"
                      @click=${() => this.move(index)}
                      aria-label="Move to slide ${index + 1}"
                    ></button>
                  </li>`
              )}
            </ul>
          `
        : ''}
    `
  }

  static styles = [
    styles.visuallyHidden,
    css`
      :host {
        position: relative;
      }
      .container {
        display: flex;
        gap: var(--hh-gap, 10px);
        position: relative;
        overflow-x: auto;
        scroll-snap-type: x mandatory;
        scroll-behavior: smooth;
        scroll-padding-left: var(--hh-scroll-padding-left, 1rem);
        -webkit-overflow-scrolling: touch;
        -ms-overflow-style: none; /* IE and Edge */
        scrollbar-width: none; /* Firefox */
      }

      .container--single-item {
        width: 100%;
        display: unset !important;
      }

      @media screen and (min-width: 750px) {
        .container--single-item {
          width: auto;
          display: flex !important;
        }

        .container {
          gap: 20px;
          padding-right: 8%;
        }
      }

      :host([active='false']) .container {
        padding: 0;
      }

      .container--no-gap {
        gap: 0;
      }

      .container::-webkit-scrollbar {
        display: none;
      }

      @media (prefers-reduced-motion: no-preference) {
        ::slotted([slot='item']) {
          opacity: 0;
        }
      }

      .carousel-buttons {
        display: none;
      }

      :host([active='true']) .carousel-buttons {
        display: flex;
        gap: var(--hh-dots-gap, 30px);
        justify-content: center;
        list-style-type: none;
        margin: var(--hh-dots-margin, 30px 0);
        padding: var(--hh-dots-padding, 0 24px);
      }

      @media screen and (max-width: 749px) {
        .hidden--sm {
          display: none !important;
        }
        .block--sm {
          display: block !important;
        }
      }

      @media screen and (min-width: 750px) and (max-width: 989px) {
        .hidden--md {
          display: none !important;
        }
        .block--md {
          display: block !important;
        }
      }

      @media screen and (min-width: 990px) {
        .hidden--lg {
          display: none !important;
        }
        .block--lg {
          display: block !important;
        }
      }

      .carousel-buttons--fullWidth {
        transform: translateX(calc(50vw - 50%));
      }

      .carousel-buttons--overlap {
        bottom: 0;
        left: 0;
        position: absolute;
        right: 0;
      }

      :host([active='true']) .carousel-buttons li {
        display: flex;
      }

      .carousel-button {
        background: transparent;
        width: var(--hh-dot-width, 50px);
        cursor: pointer;
        border: none;
        padding: var(--hh-dot-padding, 10px 0);
      }

      .carousel-button:after {
        content: '';
        background: var(--hh-color-grey-mineral);
        display: block;
        height: 2px;
        transition: all var(--hh-duration-default, 300ms);
      }

      .carousel-button--current:after {
        background: var(--hh-color-honey-two);
      }

      .arrows {
        align-items: center;
        display: flex;
        left: 0;
        position: absolute;
        right: 0;
        top: var(--hh-arrows-top, 50%);
        width: 100%;
      }

      .arrow {
        align-items: center;
        background-color: var(--hh-color-star-white);
        border: 1px solid var(--hh-color-grey-mineral);
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        height: 40px;
        justify-content: center;
        left: var(--hh-arrow-left, 24px);
        position: absolute;
        transition: background-color var(--hh-duration-default) ease;
        width: 40px;
        z-index: 5;
      }

      .button:hover {
        background-color: var(--hh-color-grey-mineral);
      }

      .arrow:disabled {
        cursor: auto;
        opacity: 0;
      }

      .arrow--next {
        left: auto;
        right: var(--hh-arrow-right, 24px);
      }

      .arrow--next svg {
        width: 26px;
      }

      .arrow--prev svg {
        transform: rotate(180deg);
        width: 26px;
      }

      .play {
        background: transparent;
        border: none;
        color: var(--hh-color-star-white);
        cursor: pointer;
        position: absolute;
        top: 5px;
        transition: color var(--hh-duration-default);
        z-index: var(--hh-z-base);
      }

      .play:hover {
        color: var(--hh-color-honey-two);
      }

      .hidden {
        display: none !important;
      }

      .block {
        display: block !important;
      }

      :host(.free-items__slider) .arrows {
        top: -44px;
      }

      :host(.free-items__slider) .arrow {
        width: 36px;
        height: 36px;
        border: 0;
        background: transparent;
        padding: 0;
        left: 10px;
      }

      :host(.free-items__slider) .arrow--next {
        right: 10px;
        left: auto;
      }

      :host(.free-items__slider) .arrow svg {
        width: 16px;
        height: 16px;
      }
    `,
  ]
}

customElements.define('hh-slider', HHSlider)
